import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, Brain, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, ThumbsUp, ThumbsDown, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRIORITY_COLORS = {
  high: 'border-red-500/50 bg-red-500/10',
  medium: 'border-yellow-500/50 bg-yellow-500/10',
  low: 'border-blue-500/50 bg-blue-500/10'
};

const PRIORITY_TEXT = {
  high: 'text-red-400',
  medium: 'text-yellow-400',
  low: 'text-blue-400'
};

const TYPE_LABELS = {
  volume_change: 'Zmiana objętości',
  intensity_change: 'Zmiana intensywności',
  deload: 'Deload',
  exercise_change: 'Zmiana ćwiczeń',
  consultation: 'Konsultacja'
};

export const AIReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [trainerNotes, setTrainerNotes] = useState({});

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const [clientRes, reportsRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients/${id}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/ai-reports/client/${id}`, { withCredentials: true })
      ]);
      setClient(clientRes.data);
      setReports(reportsRes.data);
      if (reportsRes.data.length > 0) {
        setSelectedReport(reportsRes.data[0]);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/ai-reports/generate/${id}`,
        {},
        { withCredentials: true }
      );
      setReports([response.data, ...reports]);
      setSelectedReport(response.data);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Błąd generowania raportu: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const handleApproveSuggestion = async (reportId, suggestionIndex, approved) => {
    try {
      await axios.put(
        `${API_URL}/api/ai-reports/${reportId}/approve-suggestion/${suggestionIndex}`,
        null,
        {
          params: {
            approved,
            notes: trainerNotes[suggestionIndex] || null
          },
          withCredentials: true
        }
      );
      // Refresh reports
      fetchData();
    } catch (error) {
      console.error('Error approving suggestion:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-white">Ładowanie...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="ai-report-page">
      <nav className="border-b border-[#2A2A2A] px-4 sm:px-8 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <Logo size="md" />
          <Button
            onClick={() => navigate(`/client/${id}`)}
            variant="ghost"
            className="text-[#A1A1A1] hover:text-white px-2 sm:px-4 shrink-0"
          >
            <ArrowLeft className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Powrót</span>
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-2">
            <Brain className="w-8 h-8 text-[#C9A84C]" />
            <h1 className="display-text text-5xl text-white">AI Raport</h1>
          </div>
          <p className="body-text text-[#A1A1A1] text-lg">{client?.name} • Claude Sonnet 4.6</p>
        </div>

        {/* Generate Button */}
        <div className="mb-8">
          <Button
            onClick={handleGenerateReport}
            disabled={generating}
            className="bg-[#1E1E1E] hover:bg-[#2A2A2A] text-white border border-[#C9A84C] rounded-full px-8"
            data-testid="generate-report-button"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {generating ? 'Generowanie...' : 'Wygeneruj nowy raport'}
          </Button>
        </div>

        {reports.length === 0 ? (
          <div className="card-surface rounded-3xl p-12 text-center">
            <Brain className="w-16 h-16 text-[#A1A1A1] mx-auto mb-4" />
            <p className="body-text text-[#A1A1A1] text-lg mb-4">
              Brak raportów. Wygeneruj pierwszy raport AI.
            </p>
            <p className="text-sm text-[#A1A1A1]">
              AI przeanalizuje dane monitoringu, wywiad zdrowotny i zaproponuje sugestie.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Reports List */}
            <div className="lg:col-span-1">
              <h2 className="heading-text text-xl text-white mb-4">Historia raportów</h2>
              <div className="space-y-2">
                {reports.map((report, index) => (
                  <button
                    key={report.id}
                    onClick={() => setSelectedReport(report)}
                    className={`w-full text-left p-4 rounded-2xl border transition-all ${
                      selectedReport?.id === report.id
                        ? 'bg-[#1E1E1E] border-[#C9A84C]'
                        : 'bg-[#0A0A0A] border-[#2A2A2A] hover:border-[#C9A84C]/50'
                    }`}
                  >
                    <div className="text-sm text-[#C9A84C] mb-1">
                      {new Date(report.report_date).toLocaleDateString('pl-PL')}
                    </div>
                    <div className="text-xs text-[#A1A1A1]">
                      {report.suggestions.length} sugestii • {report.status}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Report Details */}
            {selectedReport && (
              <div className="lg:col-span-3 space-y-6">
                {/* Analysis Section */}
                <div className="card-surface rounded-3xl p-8">
                  <h2 className="heading-text text-2xl text-white mb-6">Analiza</h2>

                  {/* Trends */}
                  {selectedReport.analysis.trends.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-5 h-5 text-[#C9A84C]" />
                        <span className="label-text text-[#C9A84C]">TRENDY</span>
                      </div>
                      <ul className="space-y-2">
                        {selectedReport.analysis.trends.map((trend, i) => (
                          <li key={i} className="body-text text-white flex items-start gap-2">
                            <span className="text-[#C9A84C] mt-1">•</span>
                            <span>{trend}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Alerts */}
                  {selectedReport.analysis.alerts.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                        <span className="label-text text-red-400">ALERTY</span>
                      </div>
                      <ul className="space-y-2">
                        {selectedReport.analysis.alerts.map((alert, i) => (
                          <li key={i} className="body-text text-white flex items-start gap-2">
                            <span className="text-red-400 mt-1">⚠</span>
                            <span>{alert}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Improvements */}
                  {selectedReport.analysis.improvements.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingUp className="w-5 h-5 text-green-400" />
                        <span className="label-text text-green-400">ULEPSZENIA</span>
                      </div>
                      <ul className="space-y-2">
                        {selectedReport.analysis.improvements.map((improvement, i) => (
                          <li key={i} className="body-text text-white flex items-start gap-2">
                            <span className="text-green-400 mt-1">✓</span>
                            <span>{improvement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Deteriorations */}
                  {selectedReport.analysis.deteriorations.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <TrendingDown className="w-5 h-5 text-orange-400" />
                        <span className="label-text text-orange-400">POGORSZENIA</span>
                      </div>
                      <ul className="space-y-2">
                        {selectedReport.analysis.deteriorations.map((deterioration, i) => (
                          <li key={i} className="body-text text-white flex items-start gap-2">
                            <span className="text-orange-400 mt-1">↓</span>
                            <span>{deterioration}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                {/* Suggestions Section */}
                <div>
                  <h2 className="heading-text text-2xl text-white mb-6">Sugestie AI</h2>
                  <div className="space-y-4">
                    {selectedReport.suggestions.map((suggestion, index) => (
                      <div
                        key={index}
                        className={`card-surface rounded-3xl p-6 border ${PRIORITY_COLORS[suggestion.priority]}`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`label-text text-xs ${PRIORITY_TEXT[suggestion.priority]}`}>
                                {TYPE_LABELS[suggestion.type]}
                              </span>
                              <span className={`label-text text-xs ${PRIORITY_TEXT[suggestion.priority]}`}>
                                • PRIORYTET: {suggestion.priority.toUpperCase()}
                              </span>
                            </div>
                            <p className="body-text text-white">{suggestion.description}</p>
                          </div>

                          {suggestion.approved_by_trainer ? (
                            <div className="flex items-center gap-2 ml-4">
                              <CheckCircle className="w-5 h-5 text-green-400" />
                              <span className="text-sm text-green-400">Zatwierdzone</span>
                            </div>
                          ) : (
                            <div className="flex gap-2 ml-4">
                              <Button
                                onClick={() => handleApproveSuggestion(selectedReport.id, index, true)}
                                size="sm"
                                className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border-green-500/50"
                              >
                                <ThumbsUp className="w-4 h-4" />
                              </Button>
                              <Button
                                onClick={() => handleApproveSuggestion(selectedReport.id, index, false)}
                                size="sm"
                                className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/50"
                              >
                                <ThumbsDown className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </div>

                        {/* Trainer Notes */}
                        {!suggestion.approved_by_trainer && (
                          <div className="mt-4">
                            <Textarea
                              placeholder="Notatki trenera (opcjonalne)..."
                              value={trainerNotes[index] || ''}
                              onChange={(e) => setTrainerNotes({ ...trainerNotes, [index]: e.target.value })}
                              className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white text-sm"
                              rows={2}
                            />
                          </div>
                        )}

                        {suggestion.trainer_notes && (
                          <div className="mt-4 p-3 bg-[#0A0A0A] rounded-2xl">
                            <span className="label-text text-[#A1A1A1] text-xs mb-1 block">NOTATKI TRENERA</span>
                            <p className="text-sm text-white">{suggestion.trainer_notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};